import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useStore } from "store/zustand";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";
import { getAuth } from "firebase/auth";
import { doc, getDocFromServer, deleteDoc } from "firebase/firestore";
import Select from "react-select";
import {
  applyReferralApi,
  fetchAirlinesApi,
  fetchHomeBasesApi,
  updateAccountApi,
  uploadFileApi,
  validateReferralCodeApi,
  verifyUploadApi,
} from "apis/authServices";
import { auth, firestore } from "config/firebaseConfig";
import TermsModal from "components/TermsModal";
import RegisterBg from "../../assets/images/Register.png";
import StandbyIcon from "../../assets/images/standby.png";

interface Airline {
  id: number;
  airlineName: string;
  logo: string | null;
}

interface Airport {
  id: number;
  name: string;
}

interface FormData {
  firstName: string;
  lastName: string;
  employerAirlineId: string;
  employerAirline: string;
  homebaseId: string;
  homebase: string;
  referralCode: string;
}

interface SelectOption {
  value: string;
  label: string;
}

const CreateAccount: React.FC = () => {
  const navigate = useNavigate();
  const { setIsAuthenticated } = useStore();
  const [searchParams] = useSearchParams();
  const { showLoader, hideLoader } = useGlobalLoader();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [airlines, setAirlines] = useState<Airline[]>([]);
  const [airports, setAirports] = useState<Airport[]>([]);
  const [isLoadingAirlines, setIsLoadingAirlines] = useState(false);
  const [isLoadingAirports, setIsLoadingAirports] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState<string>("");
  const [uploadVerificationMessage, setUploadVerificationMessage] =
    useState<string>("");
  const [referralCodeError, setReferralCodeError] = useState<string>("");

  const [formData, setFormData] = useState<FormData>({
    firstName: "",
    lastName: "",
    employerAirlineId: "",
    employerAirline: "",
    homebaseId: "",
    homebase: "",
    referralCode: "",
  });
  useEffect(() => {
    if (formData.referralCode === "") {
      setReferralCodeError("");
    }
  }, [formData.referralCode]);
  // Auto-fill referral code from query parameter
  useEffect(() => {
    const referralCodeFromUrl = searchParams.get("referral");
    if (referralCodeFromUrl && !formData.referralCode) {
      setFormData((prev) => ({ ...prev, referralCode: referralCodeFromUrl }));
      validateReferralCodeApi(referralCodeFromUrl)
        .then(() => setReferralCodeError(""))
        .catch(() => setReferralCodeError("Invalid referral code "));
    }
  }, [searchParams]);

  // Fetch airlines and home bases on component mount
  useEffect(() => {
    // Load initial limited data
    fetchAirlines("", 50); // Limit initial load to 50 items
    fetchHomeBases("", 50); // Limit initial load to 50 items
  }, []);

  // Debounce function for search input
  const debounce = useCallback(
    (func: (...args: any[]) => void, delay: number) => {
      let timeoutId: NodeJS.Timeout;
      return (...args: any[]) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func(...args), delay);
      };
    },
    [],
  );

  const fetchAirlines = async (query = "", limit = 100) => {
    try {
      setIsLoadingAirlines(true);
      const response = await fetchAirlinesApi(query);
      const airlineData = Array.isArray(response) ? response : [];

      // Limit the number of items to improve performance
      const limitedData = airlineData.slice(0, limit);
      setAirlines(limitedData);
    } catch (error) {
      console.error("Error fetching airlines:", error);
      setAirlines([]);
    } finally {
      setIsLoadingAirlines(false);
    }
  };

  const fetchHomeBases = async (query = "", limit = 100) => {
    try {
      setIsLoadingAirports(true);
      const response = await fetchHomeBasesApi(query);
      const airportData = Array.isArray(response) ? response : [];

      // Limit the number of items to improve performance
      const limitedData = airportData.slice(0, limit);
      setAirports(limitedData);
    } catch (error) {
      console.error("Error fetching home bases:", error);
      setAirports([]);
    } finally {
      setIsLoadingAirports(false);
    }
  };

  // Create debounced search functions
  const debouncedFetchAirlines = useCallback(
    debounce((query: string) => fetchAirlines(query, 100), 300),
    [debounce],
  );

  const debouncedFetchHomeBases = useCallback(
    debounce((query: string) => fetchHomeBases(query, 100), 300),
    [debounce],
  );

  const handleInputChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "referralCode" && formData.referralCode) {
      setReferralCodeError("");
      if (value && value.length > 1) {
        console.log(`Validating referral code: ${value}`);

        try {
          await validateReferralCodeApi(value);
          setReferralCodeError("");
        } catch (error) {
          setReferralCodeError("Invalid referral code");
        }
      }
    }
  };

  const handleSelectAirline = (selectedOption: any) => {
    setFormData((prev) => ({
      ...prev,
      employerAirlineId: selectedOption ? selectedOption.value : "",
      employerAirline: selectedOption ? selectedOption.label : "",
    }));
  };

  const handleSelectHomeBase = (selectedOption: any) => {
    setFormData((prev) => ({
      ...prev,
      homebaseId: selectedOption ? selectedOption.value : "",
      homebase: selectedOption ? selectedOption.label : "",
    }));
  };

  const validateFile = (file: File): boolean => {
    const allowedFormats = ["image/jpeg", "image/png"];
    if (!allowedFormats.includes(file.type)) {
      setUploadVerificationMessage("Only JPG and PNG files are allowed.");
      return false;
    }

    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      setVerificationMessage("Please upload an image that is 2 MB or smaller.");
      return false;
    }

    return true;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const file = event.target.files?.[0] || null;
    if (file && validateFile(file)) {
      setSelectedFile(file);
      setUploadVerificationMessage("");
    } else {
      setSelectedFile(null);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    if (file && validateFile(file)) {
      setSelectedFile(file);
      setUploadVerificationMessage("");
    }
  };

  const fetchVerificationStatusFromFirestore = async (
    userId: string,
    startTimeMs: number,
  ): Promise<{ message: string; isAirlineStaff: boolean }> => {
    const MAX_TIMEOUT = 90_000;
    const POLL_INTERVAL = 3_000;
    const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

    console.log(
      `[Firestore Debug] Starting polling for userId: ${userId} at ${new Date(startTimeMs).toISOString()}`,
    );

    const userDocRef = doc(firestore, "imageProcessingStatus", userId);
    const deadline = Date.now() + MAX_TIMEOUT;

    while (Date.now() < deadline) {
      try {
        const docSnap = await getDocFromServer(userDocRef);

        if (!docSnap.exists()) {
          console.log(`[Firestore Debug] Doc does not exist yet`);
        } else {
          const data = docSnap.data();
          const updatedAtDate: Date | null = data?.updatedAt?.toDate
            ? data.updatedAt.toDate()
            : null;
          const updatedAtMs = updatedAtDate ? updatedAtDate.getTime() : 0;

          console.log(`[Firestore Debug] Poll result:`, {
            queueStatus: data?.queueStatus,
            isAirlineStaff: data?.isAirlineStaff,
            updatedAt: updatedAtDate?.toISOString(),
          });

          // Skip stale docs from before this upload
          if (updatedAtMs > 0 && updatedAtMs < startTimeMs - 5000) {
            console.log(`[Firestore Debug] Stale doc, ignoring...`);
          } else if (
            data?.queueStatus === "completed" &&
            typeof data?.isAirlineStaff === "boolean"
          ) {
            return {
              message: data?.description || "Verification status received.",
              isAirlineStaff: data.isAirlineStaff,
            };
          }
        }
      } catch (e: any) {
        console.log(`[Firestore Debug] Poll error: ${e.message}`);
      }

      await wait(POLL_INTERVAL);
    }

    throw new Error("Image verification is taking longer than expected. Please try again or upload a different image.");
  };

  const handleSubmit = async () => {
    if (!formData.firstName.trim()) {
      setVerificationMessage("Please enter your first name.");
      return;
    }

    if (!formData.lastName.trim()) {
      setVerificationMessage("Please enter your last name.");
      return;
    }

    if (!formData.employerAirlineId) {
      setVerificationMessage("Please select your employer airline.");
      return;
    }

    if (!formData.homebaseId) {
      setVerificationMessage("Please select your home base.");
      return;
    }

    if (!selectedFile) {
      setVerificationMessage("Please upload your proof of employment.");
      return;
    }

    if (!termsAccepted) {
      setVerificationMessage("Terms and conditions must be accepted.");
      return;
    }

    if (referralCodeError) {
      setVerificationMessage(
        "Please enter a valid referral code or leave it empty.",
      );
      return;
    }

    setIsLoading(true);
    setVerificationMessage("");
    showLoader();
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error("User not authenticated. Please log in.");
      }
      const uid = user.uid;
      console.log("UserId: " + uid);

      if (selectedFile) {
        const uploadStartTimeMs = Date.now();

        // Try to delete stale doc (best effort — may fail due to security rules)
        try {
          await deleteDoc(doc(firestore, "imageProcessingStatus", uid));
          console.log("[Firestore Debug] Cleared stale imageProcessingStatus doc.");
        } catch (e) {
          console.log("[Firestore Debug] No stale doc to delete or delete failed:", e);
        }

        const uploadResponse = await uploadFileApi(selectedFile);
        if (!uploadResponse || typeof uploadResponse !== "object") {
          throw new Error("Invalid or undefined upload response");
        }
        console.log("Upload response: ", uploadResponse);

        const { fileUrl, userId, message: uploadMessage } = uploadResponse;
        if (
          fileUrl === undefined ||
          fileUrl === null ||
          userId === undefined ||
          userId === null
        ) {
          throw new Error("Missing fileUrl or userId in upload response");
        }

        setVerificationMessage(uploadResponse.message);

        const verifyResponse = await verifyUploadApi(
          uploadResponse.fileUrl,
          uploadResponse.userId,
        );

        if (!verifyResponse?.message) {
          throw new Error("Invalid verification response");
        }

        const { message, isAirlineStaff } =
          await fetchVerificationStatusFromFirestore(uid, uploadStartTimeMs);

        if (isAirlineStaff === false) {
          const detail = (message || "").trim();
          const friendly = detail
            ? `We couldn't verify your image: ${detail.charAt(0).toUpperCase() + detail.slice(1)} Please upload a clear photo of your airline ID or proof of employment.`
            : "We couldn't verify your image. Please upload a clear photo of your airline ID or proof of employment.";
          throw new Error(friendly);
        }

        if (isAirlineStaff !== true) {
          throw new Error("Unexpected verification status. Please try again.");
        }

        setVerificationMessage(message);
      }

      await updateAccountApi(
        formData.firstName,
        formData.lastName,
        Number(formData.employerAirlineId),
        Number(formData.homebaseId),
        formData.referralCode,
      );

      // Apply referral credit if a referral code was supplied
      if (formData.referralCode?.trim()) {
        try {
          await applyReferralApi(formData.referralCode.trim());
        } catch (referralError: any) {
          console.error("Failed to apply referral:", referralError);
          // Don't block signup — user is already created.
          // Only surface the "already applied" case on the home page.
          const message: string = referralError?.message || "";
          if (message.toLowerCase().includes("already")) {
            sessionStorage.setItem("referralMessage", message);
          }
        }
      }

      setIsAuthenticated(true);
      navigate("/");
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.response?.data?.title;
      setVerificationMessage(apiMessage || error.message || "Image verification failed. Please try again.");
    } finally {
      hideLoader();
      setIsLoading(false);
    }
  };

  // Transform airlines and airports into react-select options with memoization
  const airlineOptions = useMemo(
    () =>
      airlines.map((airline) => ({
        value: airline.id.toString(),
        label: airline.airlineName,
      })),
    [airlines],
  );

  const airportOptions = useMemo(
    () =>
      airports.map((airport) => ({
        value: airport.id.toString(),
        label: airport.name,
      })),
    [airports],
  );

  // Find selected values for react-select with fallback (memoized)
  const selectedAirline = useMemo(
    () =>
      formData.employerAirlineId
        ? airlineOptions.find(
            (option) => option.value === formData.employerAirlineId,
          ) || {
            value: formData.employerAirlineId,
            label: formData.employerAirline || formData.employerAirlineId,
          }
        : null,
    [formData.employerAirlineId, formData.employerAirline, airlineOptions],
  );

  const selectedHomeBase = useMemo(
    () =>
      formData.homebaseId
        ? airportOptions.find(
            (option) => option.value === formData.homebaseId,
          ) || {
            value: formData.homebaseId,
            label: formData.homebase || formData.homebaseId,
          }
        : null,
    [formData.homebaseId, formData.homebase, airportOptions],
  );

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 50 }}>
      {/* Full-page Background Image */}
      <img
        src={RegisterBg}
        alt="Register background"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.35)",
        }}
      />

      {/* Content Layer */}
      <div
        style={{
          position: "relative",
          zIndex: 10,
          display: "flex",
          width: "100%",
          height: "100%",
        }}
      >
        {/* Left - Logo & Tagline */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            padding: "2.5rem",
          }}
        >
          <div className="flex items-center gap-2">
            <img src={StandbyIcon} alt="Standby" className="w-8 h-8" />
            <span className="text-white text-xl font-bold tracking-wide">
              STANDBY
            </span>
          </div>
          <div className="mb-10">
            <h1 className="text-white text-4xl font-bold leading-tight">
              Connecting <span className="text-light-yellow">Air Crews,</span>
              <br />
              <span className="italic">Seamlessly.</span>
            </h1>
          </div>
        </div>

        {/* Right - Form Card */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "2rem",
            minWidth: "640px",
            maxWidth: "720px",
          }}
        >
          <div className="w-full">
            <div
              className="bg-white rounded-2xl shadow-lg px-12 py-12 sm:px-14 sm:py-14 overflow-y-auto"
              style={{ maxHeight: "95vh" }}
            >
              <div className="flex justify-center">
                <span className="inline-block px-4 py-1 rounded-full border border-gray-200 text-xs text-grease-100">
                  Step 2 of 2
                </span>
              </div>
              <h2 className="text-2xl font-bold text-grease-100 text-center mt-4">
                Create An Account
              </h2>
              {verificationMessage && (
                <p className="mt-4 text-sm text-red-700 bg-red-50 p-3 rounded-lg">
                  {verificationMessage}
                </p>
              )}

              <div className="mt-6 flex items-center gap-4">
                <div className="w-full">
                  <label className="block text-sm font-medium text-grease-100 mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    id="firstName"
                    className="block w-full p-3.5 rounded-lg bg-white text-sm text-gray-800
                      focus:outline-none focus:ring-1 focus:ring-light-yellow
                      border border-gray-200 focus:border-light-yellow"
                    placeholder="Enter First Name"
                  />
                </div>
                <div className="w-full">
                  <label className="block text-sm font-medium text-grease-100 mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    id="lastName"
                    className="block w-full p-3.5 rounded-lg bg-white text-sm text-gray-800
                      focus:outline-none focus:ring-1 focus:ring-light-yellow
                      border border-gray-200 focus:border-light-yellow"
                    placeholder="Enter Last Name"
                  />
                </div>
              </div>

              <div className="mt-4 relative">
                <label className="block text-sm font-medium text-grease-100 mb-2">
                  Your Employer Airline
                </label>
                <i
                  className="st-flight text-base absolute left-4 top-[44px] z-10 pointer-events-none inline-block"
                  style={{
                    transform: "rotate(-50deg)",
                    color: "transparent",
                    WebkitTextStroke: "0.75px #868686",
                  }}
                ></i>
                <Select
                  className="basic-single"
                  classNamePrefix="select"
                  value={selectedAirline}
                  isLoading={isLoadingAirlines}
                  isClearable={true}
                  isSearchable={true}
                  name="employerAirlineId"
                  options={airlineOptions}
                  onChange={handleSelectAirline}
                  onInputChange={(inputValue, { action }) => {
                    if (
                      action === "input-change" &&
                      inputValue.trim().length > 1
                    ) {
                      debouncedFetchAirlines(inputValue);
                    }
                  }}
                  placeholder={
                    <span>
                      Select Employer Airline{" "}
                      <span style={{ color: "red" }}>*</span>
                    </span>
                  }
                  noOptionsMessage={({ inputValue }) =>
                    isLoadingAirlines
                      ? "Loading..."
                      : inputValue
                        ? `No airlines found for "${inputValue}"`
                        : "Type to search airlines"
                  }
                  maxMenuHeight={200}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      outline: "none !important",
                      backgroundColor: "#ffffff",
                      border: state.isFocused
                        ? "1px solid #FFBC00"
                        : "1px solid #e5e7eb",
                      padding: "0.35rem",
                      paddingLeft: "1.75rem",
                      borderRadius: "0.5rem",
                      boxShadow: state.isFocused ? "0 0 0 1px #FFBC00" : "none",
                      "&:hover": {
                        borderColor: state.isFocused ? "#FFBC00" : "#CBD5E0",
                      },
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: "#A0AEC0",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.25rem",
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: "#2D3748",
                    }),
                    option: (base, state) => ({
                      ...base,
                      color: "#2D3748",
                      backgroundColor: state.isSelected
                        ? "#FCD34D"
                        : state.isFocused
                          ? "#FDE68A"
                          : "#FFFFFF",
                      "&:hover": {
                        backgroundColor: "#FDE68A",
                      },
                    }),
                  }}
                  filterOption={null}
                />
                {airlines.length === 0 && !isLoadingAirlines && (
                  <p className="text-sm text-gray-500 mt-1">
                    Start typing to search airlines
                  </p>
                )}
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-grease-100 mb-2">
                  Your HomeBase
                </label>
                <Select
                  className="basic-single"
                  classNamePrefix="select"
                  value={selectedHomeBase}
                  isLoading={isLoadingAirports}
                  isClearable={true}
                  isSearchable={true}
                  name="homebaseId"
                  options={airportOptions}
                  onChange={handleSelectHomeBase}
                  onInputChange={(inputValue, { action }) => {
                    if (
                      action === "input-change" &&
                      inputValue.trim().length > 1
                    ) {
                      debouncedFetchHomeBases(inputValue);
                    }
                  }}
                  placeholder={
                    <span>
                      Enter Your HomeBase{" "}
                      <span style={{ color: "red" }}>*</span>
                    </span>
                  }
                  noOptionsMessage={({ inputValue }) =>
                    isLoadingAirports
                      ? "Loading..."
                      : inputValue
                        ? `No home bases found for "${inputValue}"`
                        : "Type to search home bases"
                  }
                  maxMenuHeight={200}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      outline: "none !important",
                      backgroundColor: "#ffffff",
                      border: state.isFocused
                        ? "1px solid #FFBC00"
                        : "1px solid #e5e7eb",
                      padding: "0.35rem",
                      borderRadius: "0.5rem",
                      boxShadow: state.isFocused ? "0 0 0 1px #FFBC00" : "none",
                      "&:hover": {
                        borderColor: state.isFocused ? "#FFBC00" : "#CBD5E0",
                      },
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: "#A0AEC0",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.25rem",
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: "#2D3748",
                    }),
                    option: (base, state) => ({
                      ...base,
                      color: "#2D3748",
                      backgroundColor: state.isSelected
                        ? "#FCD34D"
                        : state.isFocused
                          ? "#FDE68A"
                          : "#FFFFFF",
                      "&:hover": {
                        backgroundColor: "#FDE68A",
                      },
                    }),
                    input: (base) => ({
                      ...base,
                      outline: "none !important",
                      color: "#2D3748",
                      "& input": {
                        outline: "none !important",
                        boxShadow: "none !important",
                        border: "none !important",
                      },
                    }),
                  }}
                  filterOption={null}
                />
                {airports.length === 0 && !isLoadingAirports && (
                  <p className="text-sm text-gray-500 mt-1">
                    Start typing to search home bases
                  </p>
                )}
              </div>

              <div className="mt-4">
                <input
                  type="text"
                  name="referralCode"
                  value={formData.referralCode}
                  onChange={handleInputChange}
                  className="block w-full p-3.5 rounded-lg bg-white text-sm text-gray-800 placeholder-gray-400
                    border border-gray-200
                    focus:outline-none
                    focus:ring-1
                    focus:ring-light-yellow
                    focus:border-light-yellow"
                  placeholder="Referral Code (Optional)"
                />
                {referralCodeError && (
                  <p className="mt-2 text-sm text-red-600">
                    {referralCodeError}
                  </p>
                )}
              </div>

              <p className="font-medium text-sm text-grease-100 mt-5">
                Upload Proof
                <span className="text-red-500 ml-1">*</span>
              </p>
              <div className="w-full mt-3">
                <label
                  htmlFor="dropzone-file"
                  className={`flex items-center justify-between w-full px-6 py-5 border-2 border-light-yellow border-dashed rounded-lg cursor-pointer hover:bg-yellow-50 ${
                    isDragging ? "bg-yellow-50" : ""
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <div className="flex flex-col items-center gap-2 flex-1 min-w-0">
                    <img className="w-7" src="/images/upload.svg" alt="Upload" />
                    <p className="text-xs text-grease-100 text-center truncate max-w-full">
                      {selectedFile ? selectedFile.name : "Drag your file(s) to start uploading"}
                    </p>
                  </div>
                  <div className="flex items-center gap-4 flex-shrink-0">
                    <div className="flex flex-col items-center">
                      <div className="w-px h-4 bg-gray-200" />
                      <span className="text-gray-400 text-xs my-1">or</span>
                      <div className="w-px h-4 bg-gray-200" />
                    </div>
                    <button
                      type="button"
                      className="font-semibold px-4 py-2 rounded-lg text-grease-100 text-center border border-gray-300 text-xs hover:bg-gray-50 bg-white"
                      onClick={(e) => {
                        e.preventDefault();
                        document.getElementById("dropzone-file")?.click();
                      }}
                    >
                      Browse files
                    </button>
                  </div>
                  <input id="dropzone-file" type="file" className="hidden" onChange={handleFileChange} accept="image/jpeg,image/jpg,image/png" />
                </label>
              </div>
              {uploadVerificationMessage && (
                <p className="mt-2 text-sm text-red-600">
                  {uploadVerificationMessage}
                </p>
              )}

              <div className="flex items-center mt-5">
                <input
                  id="checked-checkbox"
                  type="checkbox"
                  checked={termsAccepted}
                  onClick={(e) => {
                    if (!termsAccepted) {
                      e.preventDefault();
                      setIsTermsModalOpen(true);
                    }
                  }}
                  onChange={(e) => {
                    if (termsAccepted) {
                      setTermsAccepted(e.target.checked);
                    }
                  }}
                  className="w-4 h-4 text-light-yellow bg-gray-100 border-gray-300 rounded focus:ring-light-yellow cursor-pointer"
                />
                <label
                  htmlFor="checked-checkbox"
                  className="ml-2 text-xs font-medium text-grease-100"
                >
                  I accept the{" "}
                  <a
                    onClick={(e) => {
                      e.preventDefault();
                      setIsTermsModalOpen(true);
                    }}
                    className="underline text-light-yellow hover:text-yellow-600 cursor-pointer"
                  >
                    terms & conditions
                  </a>
                </label>
              </div>
              <TermsModal
                isOpen={isTermsModalOpen}
                onClose={() => setIsTermsModalOpen(false)}
                onAccept={() => {
                  setTermsAccepted(true);
                  setIsTermsModalOpen(false);
                }}
              />
              <button
                onClick={handleSubmit}
                disabled={isLoading || !termsAccepted}
                className="w-full p-3.5 rounded-lg text-[#192227] text-center font-semibold shadow-button bg-light-yellow text-sm mt-5 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
              >
                {isLoading ? "Submitting..." : "Submit"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateAccount;
